import React, { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";

export const autoplayAllowed = () => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return false;
  if (navigator.connection?.saveData) return false;
  return true;
};

export const useInView = (ref, { root, rootMargin = "0px" } = {}) => {
  const [inView, setInView] = useState(false);
  const obs = useRef({ el: null, io: null });
  // No deps: re-checks after each render so elements mounted later (e.g. after data loads) get observed.
  useEffect(() => {
    const el = ref.current;
    if (el === obs.current.el || typeof IntersectionObserver === "undefined") return;
    obs.current.io?.disconnect();
    obs.current = { el, io: null };
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), {
      root: root?.current || null,
      rootMargin,
    });
    io.observe(el);
    obs.current.io = io;
  });
  useEffect(() => () => {
    obs.current.io?.disconnect();
    obs.current = { el: null, io: null };
  }, []);
  return inView;
};

const PAUSE_KEY = "ur:carousel-paused";

// Visitor's carousel pause preference, remembered for the browser session.
export const useCarouselPause = () => {
  const [paused, setPaused] = useState(() => {
    try { return sessionStorage.getItem(PAUSE_KEY) === "1"; } catch { return false; }
  });
  const toggle = () => setPaused((p) => {
    try { sessionStorage.setItem(PAUSE_KEY, p ? "0" : "1"); } catch {}
    return !p;
  });
  return [paused, toggle];
};

export const CarouselPauseButton = ({ paused, onToggle, className = "", testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onToggle}
    aria-pressed={paused}
    aria-label={paused ? "Play carousel animation" : "Pause carousel animation"}
    title={paused ? "Play" : "Pause"}
    className={`rounded-full bg-[#1A1A1A] border border-[#262626] grid place-items-center text-[#A3A3A3] hover:text-white hover:border-[#FF6600] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF6600] transition-colors ${className}`}
  >
    {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
  </button>
);

// Cover image + muted looping video that plays whenever the card is visible.
export const FeaturedCardMedia = ({ project, rootRef, near, visible, paused, alt, imgClassName = "", imgProps = {}, testid }) => {
  const boxRef = useRef(null);
  const videoRef = useRef(null);
  const [allowed] = useState(autoplayAllowed);
  const [showVideo, setShowVideo] = useState(false);
  const [failed, setFailed] = useState(false);
  const src = project.homepage_video_cover;
  const enabled = !!src && allowed && !failed;
  const cardNear = useInView(boxRef, { root: rootRef, rootMargin: "0px 50%" });
  const cardVisible = useInView(boxRef, { root: rootRef });
  // Video is mounted only while the card is near the carousel viewport; far cards release it.
  const armed = enabled && near && cardNear;
  const shouldPlay = armed && visible && cardVisible && !paused;

  useEffect(() => {
    if (!armed) setShowVideo(false);
  }, [armed]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (shouldPlay) {
      v.muted = true;
      const p = v.play();
      if (p?.catch) p.catch(() => {});
    } else {
      v.pause();
    }
  }, [shouldPlay]);

  // Release the network/decoder on unmount; restore src if a dev StrictMode remount cleared it.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    if (!v.getAttribute("src") && src) v.setAttribute("src", src);
    return () => {
      v.pause();
      v.removeAttribute("src");
      v.load();
    };
  }, [armed, src]);

  return (
    <div ref={boxRef} className="aspect-[4/3] overflow-hidden relative" data-testid={testid}>
      <img src={project.image} alt={alt} className={`w-full h-full object-cover ${imgClassName}`} draggable={false} {...imgProps} />
      {armed && (
        <video
          ref={videoRef}
          src={src}
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          disableRemotePlayback
          aria-hidden="true"
          tabIndex={-1}
          data-testid={testid ? `${testid}-video` : undefined}
          data-playing={showVideo ? "true" : "false"}
          onPlaying={() => setShowVideo(true)}
          onError={() => setFailed(true)}
          className={`absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-300 ${showVideo ? "opacity-100" : "opacity-0"}`}
        />
      )}
    </div>
  );
};
